import {
  AdminInput,
  AdminSpinner,
  Disclaimer,
  Div,
  getAllVerifiedLocations,
  getPartnerLocationsWithBrands,
  H5,
  Img,
  isAdmin,
  lightTheme,
  P,
  restaurantImage,
  SimpleLocationEntity,
  SimpleLocationWithTodayDataResponse,
  reportError,
} from '@vgrubs/components';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export const LocationPart = ({
  handleSelectLocation,
  isLocationSelected,
}: {
  handleSelectLocation: (location: SimpleLocationEntity) => void;
  isLocationSelected: (location: SimpleLocationEntity) => boolean;
}) => {
  const [data, setData] = useState<SimpleLocationEntity[]>([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [ref, inView] = useInView();
  const [hasMore, setHasMore] = useState(true);
  async function getLocations(
    search: boolean,
    page: number,
    hasMore: boolean,
    query: string
  ) {
    if (!hasMore) return;
    try {
      setLoading(true);

      const params = isAdmin
        ? {
            query,
            page,
            limit: 10,
          }
        : {
            query,
            page,
            limit: 10,
            verified: true,
          };

      const getFunction = isAdmin
        ? getAllVerifiedLocations
        : getPartnerLocationsWithBrands;

      const { data } = await getFunction(params);

      const dataLocations = isAdmin
        ? (data.data as SimpleLocationEntity[])
        : (data.data as SimpleLocationWithTodayDataResponse[]).map(
            ({ location }) => location
          );

      if (!search) {
        if (dataLocations[0]) {
          setHasMore(true);
          setData((prevItems) => [...prevItems, ...dataLocations]);
        } else {
          setHasMore(false);
        }
      } else {
        if (dataLocations[0]) {
          setHasMore(true);
          setData(() => [...dataLocations]);
        } else {
          setData(() => [...dataLocations]);
          setHasMore(false);
        }
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      setHasMore(false);
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasMore(true);
    setPage(1);
    setQuery(e.target.value);
    getLocations(true, 1, true, e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  useEffect(() => {
    if (data.length < 1) {
      getLocations(false, page, hasMore, query);
    }
  }, []);

  useEffect(() => {
    if (inView && data.length > 0) {
      getLocations(false, page, hasMore, query);
    }
  }, [inView]);

  return (
    <>
      <Div $mb={14}>
        <AdminInput
          search
          onChange={debouncedResults}
          placeholder="Search Location"
        />
      </Div>
      {data.length === 0 && !hasMore && (
        <Div
          $flex
          $center
          $column
          $height="calc(70vh - 300px)"
          $heightSM="calc(60vh - 300px)"
        >
          <Img
            src={restaurantImage}
            alt="restaurant"
            $width="250px"
            $widthSM="150px"
          />
          <H5>No Locations</H5>
        </Div>
      )}
      {data.map((lc, i) => {
        const l = lc as SimpleLocationEntity;
        return (
          <Div
            key={i}
            $box
            $p="14px 24px"
            $mb="8"
            $flex
            $alignCenter
            $between
            onClick={() => handleSelectLocation(l)}
            $gap="16px"
            $pointer
            // delay={0.1}
          >
            <div>
              <P
                $left
                $bold
                $mb="8"
                $color={isLocationSelected(l) && lightTheme.primary}
                $lineHeight="1.2"
              >
                {l.name}
              </P>
              <Disclaimer $left $mb="0">
                {l.address}
              </Disclaimer>
            </div>
          </Div>
        );
      })}
      <div ref={ref}></div>
      {loading && <AdminSpinner absolute small />}
    </>
  );
};
